import { remarksSchema } from './common';
import {
  birthdayWithHyphen,
  emailDigits,
  halfWidthWithMailSymbol,
  phoneDigits,
} from './regex';

import {
  boolean,
  InferType,
  number,
  object,
  string,
} from '@/validations/custom-yup';

const CHARGE_AMOUNT_UNIT = 1000;
const SETTLEMENT_AMOUNT_UNIT = 1000;

export const phoneWalletUserSchema = string()
  .label('電話番号')
  .when('phoneAuthInit', {
    is: true,
    then: (it) => it.required(),
    otherwise: (it) => it.notRequired(),
  })
  .test(
    'phone',
    '電話番号は数字のみで10文字以上、11文字以下で入力してください。',
    (value) => {
      if (value) {
        return phoneDigits.test(value);
      }
      return true;
    },
  );

// 電話番号の入力が必須かどうかの判定に使う
export const phoneAuthInitSchema = boolean();

/** 外部ユーザID */
export const externalUserIdSchema = string()
  .label('外部ユーザID')
  .notRequired()
  .max(100)
  .matches(halfWidthWithMailSymbol, '外部ユーザIDは半角英数字または記号（@.-_）で入力してください。');

/** メールアドレス
 * アカウントとの命名重複回避のためWalletUserを追加
*/
export const emailAddressWalletUserSchema = string()
  .label('メールアドレス')
  .when('emailAuthInit', {
    is: true,
    then: (it) => it.required(),
    otherwise: (it) => it.notRequired(),
  })
  .email('メールアドレスのフォーマットを確認してください。')
  .test(
    'email',
    'メールアドレスは6~100文字で入力してください。',
    (value) => {
      if (value) {
        return emailDigits.test(value);
      }
      return true;
    },
  );

// メールアドレスの入力が必須かどうかの判定に使う
export const emailAuthInitSchema = boolean();

/** 姓 */
export const lastNameSchema = string()
  .label('姓')
  .max(64)
  .notRequired();

/** 名 */
export const firstNameSchema = string()
  .label('名')
  .max(64)
  .notRequired();

/** 姓カナ */
export const lastNameKanaSchema = string()
  .label('姓カナ')
  .max(64)
  .notRequired();

/** 名カナ */
export const firstNameKanaSchema = string()
  .label('名カナ')
  .max(64)
  .notRequired();

/** 生年月日 */
export const birthdaySchema = string()
  .label('生年月日')
  .notRequired()
  .matches(birthdayWithHyphen, '生年月日を正しい形式で入力してください。');

/** チャージ通知設定 */
export const chargeNoticeSchema = string()
  .label('チャージ通知設定')
  .required();

/** 決済通知設定 */
export const settlementNoticeSchema = string()
  .label('決済通知設定')
  .required();

/** その他残高変動に伴う通知設定 */
export const balanceNoticeSchema = string()
  .label('その他残高変動に伴う通知設定')
  .required();

/** ログインロック通知設定 */
export const loginLockNoticeSchema = string()
  .label('ログインロック通知設定')
  .required();

/** チャージ金額上限/回
 * shopとの命名重複回避のためPerTransactionを追加
*/

export const maxChargeAmountPerTransactionSchema = number()
  .label('チャージ金額上限/回')
  .required()
  .min(0, '1回あたりのチャージ金額上限は0~2,147,483,000以内で入力してください。')
  .max(2147483000, '1回あたりのチャージ金額上限は0~2,147,483,000以内で入力してください。')
  .isValidTransactionUnit(CHARGE_AMOUNT_UNIT, `1回あたりのチャージ金額上限は${CHARGE_AMOUNT_UNIT}円単位で入力してください。`);

/** チャージ金額上限/日 */
export const maxChargeAmountPerDaySchema = number()
  .label('チャージ金額上限/日')
  .required()
  .min(0, '1日あたりのチャージ金額上限は0~2,147,483,000以内で入力してください。')
  .max(2147483000, '1日あたりのチャージ金額上限は0~2,147,483,000以内で入力してください。')
  .isValidTransactionUnit(CHARGE_AMOUNT_UNIT, `1日あたりのチャージ金額上限は${CHARGE_AMOUNT_UNIT}円単位で入力してください。`);

/** チャージ金額上限/月 */
export const maxChargeAmountPerMonthSchema = number()
  .label('チャージ金額上限/月')
  .required()
  .min(0, '1月あたりのチャージ金額上限は0~2,147,483,000以内で入力してください。')
  .max(2147483000, '1月あたりのチャージ金額上限は0~2,147,483,000以内で入力してください。')
  .isValidTransactionUnit(CHARGE_AMOUNT_UNIT, `1月あたりのチャージ金額上限は${CHARGE_AMOUNT_UNIT}円単位で入力してください。`);

const maxChargeAmountSettingsSchema = object({
  maxChargeAmount: maxChargeAmountPerTransactionSchema,
  maxChargeAmountPerDay: maxChargeAmountPerDaySchema,
  maxChargeAmountPerMonth: maxChargeAmountPerMonthSchema,
}).default(undefined);

/** 決済金額上限/回
 * shopとの命名重複回避のためPerTransactionを追加
*/
export const maxSettlementAmountPerTransactionSchema = number()
  .label('決済金額上限/回')
  .required()
  .min(0, '1回あたりの決済金額上限は0~2,147,483,000以内で入力してください。')
  .max(2147483000, '1回あたりの決済金額上限は0~2,147,483,000以内で入力してください。')
  .isValidTransactionUnit(SETTLEMENT_AMOUNT_UNIT, `1回あたりの決済金額上限は${SETTLEMENT_AMOUNT_UNIT}円単位で入力してください。`);

/** 決済金額上限/日 */
export const maxSettlementAmountPerDaySchema = number()
  .label('決済金額上限/日')
  .required()
  .min(0, '1日あたりの決済金額上限は0~2,147,483,000以内で入力してください。')
  .max(2147483000, '1日あたりの決済金額上限は0~2,147,483,000以内で入力してください。')
  .isValidTransactionUnit(SETTLEMENT_AMOUNT_UNIT, `1日あたりの決済金額上限は${SETTLEMENT_AMOUNT_UNIT}円単位で入力してください。`);

/** 決済金額上限/月 */
export const maxSettlementAmountPerMonthSchema = number()
  .label('決済金額上限/月')
  .required()
  .min(0, '1月あたりの決済金額上限は0~2,147,483,000以内で入力してください。')
  .max(2147483000, '1月あたりの決済金額上限は0~2,147,483,000以内で入力してください。')
  .isValidTransactionUnit(SETTLEMENT_AMOUNT_UNIT, `1月あたりの決済金額上限は${SETTLEMENT_AMOUNT_UNIT}円単位で入力してください。`);

export const sexSchema = string<'FEMALE' | 'MALE' | 'NOT_KNOWN' | 'UNSPECIFIED'>()
  .label('性別');

export const walletUserEditFormSchema = object({
  phone: phoneWalletUserSchema,
  phoneAuthInit: phoneAuthInitSchema,
  externalUserId: externalUserIdSchema,
  firstName: firstNameSchema,
  firstNameKana: firstNameKanaSchema,
  lastName: lastNameSchema,
  lastNameKana: lastNameKanaSchema,
  remarks: remarksSchema(700),
  email: emailAddressWalletUserSchema,
  emailAuthInit: emailAuthInitSchema,
  birthday: birthdaySchema,
  chargeNotice: chargeNoticeSchema,
  settlementNotice: settlementNoticeSchema,
  balanceNotice: balanceNoticeSchema,
  loginLockNotice: loginLockNoticeSchema,
  maxChargeSettings: maxChargeAmountSettingsSchema,
  maxSettlementAmount: maxSettlementAmountPerTransactionSchema,
  maxSettlementAmountPerDay: maxSettlementAmountPerDaySchema,
  maxSettlementAmountPerMonth: maxSettlementAmountPerMonthSchema,
  sex: sexSchema,
});

export type WalletUserValidationEditForm = InferType<typeof walletUserEditFormSchema>;
